import { Component } from '@theme/component';
import { ThemeEvents, CartErrorEvent, CartUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLInputElement} stickyQuantityInput - Sticky bar quantity input
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'stickyQuantityInput'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    // The always-visible product bar still needs the standard product form target.
    this.#cacheTargetAddToCartButton();

    if (this.dataset.forceVisible !== 'true') {
      this.#setupIntersectionObserver();
    } else {
      this.#showStickyBar();
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#syncMainQuantity(this.#currentQuantity);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#targetAddToCartButton) {
      this.#syncMainQuantity(this.#currentQuantity);
      this.#targetAddToCartButton.dataset.puppet = 'true';
      this.#targetAddToCartButton.click();
    } else {
      const added = await this.#addCurrentVariantToCart();
      if (!added) return;
    }

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  async #addCurrentVariantToCart() {
    const variantId = Number(this.dataset.currentVariantId);
    const quantity = this.#currentQuantity;
    const button = this.refs.addToCartButton;
    if (!variantId || !quantity || button.disabled) return false;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const body = new URLSearchParams({
        id: String(variantId),
        quantity: String(quantity),
      });
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body,
      });
      const addType = addResponse.headers.get('content-type') || '';
      const addResult = /json|javascript/i.test(addType) ? await addResponse.json() : null;
      if (!addResponse.ok || addResult?.status) {
        const fallbackMessage =
          addResponse.status === 429
            ? 'Your connection is being checked. Please wait a moment and try again.'
            : 'Unable to add this item to the cart.';
        throw new Error(addResult?.description || addResult?.message || fallbackMessage);
      }
      if (!addResult?.variant_id && !addResult?.id) {
        throw new Error('The cart did not confirm this item. Please try again.');
      }

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });
      const cartType = cartResponse.headers.get('content-type') || '';
      if (!cartResponse.ok || !/json|javascript/i.test(cartType)) {
        throw new Error('Unable to refresh the cart.');
      }
      const cart = await cartResponse.json();

      this.dispatchEvent(
        new CartUpdateEvent(cart, String(variantId), {
          source: 'sticky-add-to-cart',
          itemCount: cart.item_count,
          variantId: String(variantId),
        })
      );
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add this item to the cart.';
      this.dispatchEvent(new CartErrorEvent('sticky-add-to-cart', message, message, {}));
      console.error(message);
      return false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  increaseStickyQuantity = () => {
    this.#adjustStickyQuantity(1);
  };

  decreaseStickyQuantity = () => {
    this.#adjustStickyQuantity(-1);
  };

  handleStickyQuantityBlur = () => {
    const nextValue = parseInt(this.refs.stickyQuantityInput.value || `${this.#currentQuantity}`, 10);
    this.#setQuantity(nextValue);
  };

  selectStickyQuantity = () => {
    this.refs.stickyQuantityInput.select();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Re-clamp quantity against the new variant rules after morphing.
    this.#updateButtonText();
    this.#setQuantity(this.#currentQuantity, false);
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#setQuantity(event.detail.quantity, false);
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #cacheTargetAddToCartButton() {
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the sticky quantity field.
   */
  #updateButtonText() {
    this.refs.stickyQuantityInput.value = `${this.#currentQuantity}`;
  }

  #adjustStickyQuantity(direction) {
    const input = this.refs.stickyQuantityInput;
    const step = parseInt(input.getAttribute('step') || '1', 10) || 1;
    const nextValue = this.#currentQuantity + direction * step;
    this.#setQuantity(nextValue);
  }

  #setQuantity(nextValue, syncMain = true) {
    const normalized = this.#normalizeQuantity(nextValue);
    this.#currentQuantity = normalized;
    this.#updateButtonText();
    if (syncMain) {
      this.#syncMainQuantity(normalized);
    }
  }

  #normalizeQuantity(nextValue) {
    const input = this.refs.stickyQuantityInput;
    const min = parseInt(input.getAttribute('min') || '1', 10) || 1;
    const maxAttr = input.getAttribute('max');
    const max = maxAttr ? parseInt(maxAttr, 10) : null;
    const step = parseInt(input.getAttribute('step') || '1', 10) || 1;

    let normalized = Number.isFinite(nextValue) ? nextValue : min;
    normalized = Math.max(min, normalized);

    if (max != null && !Number.isNaN(max)) {
      normalized = Math.min(max, normalized);
    }

    const stepsFromMin = Math.round((normalized - min) / step);
    normalized = min + Math.max(0, stepsFromMin) * step;

    if (max != null && !Number.isNaN(max)) {
      normalized = Math.min(max, normalized);
    }

    return normalized;
  }

  #syncMainQuantity(quantity) {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const quantitySelector = productForm.querySelector('quantity-selector-component');
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(quantity);
      return;
    }

    const input = productForm.querySelector('input[name="quantity"]');
    if (!input) return;
    input.value = `${quantity}`;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
